import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { User } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { courseQueryKey } from '@/features/courses/useCourse';

import {
  createStaffInviteService,
  type CreatedStaffInvite,
  type CreateStaffInviteInput,
} from './staffInviteService';
import type { StaffInvite, StaffRole } from './staffInviteTypes';

export const staffInvitesQueryKey = (courseId: string) =>
  ['course-staff-invites', courseId] as const;

// Everything the Add Instructor dialog needs for invites and removing staff.
// The pending list loads only once the dialog asks for it (refresh).
export interface CourseStaffInvitesController {
  cancelInvite(key: string): Promise<void>;
  // Whether the signed-in person is the course's primary instructor, the only
  // one who may remove staff.
  canRemoveStaff: boolean;
  createInvite(input: CreateStaffInviteInput): Promise<CreatedStaffInvite>;
  currentUserId: string | null;
  invites: StaffInvite[];
  invitesError: ApiError | null;
  isCreating: boolean;
  isLoadingInvites: boolean;
  // Loads the course's staff and its pending invitations again.
  refresh(): void;
  removeStaffMember(staffUserId: string, role: StaffRole): Promise<void>;
  renewInvite(key: string): Promise<StaffInvite>;
}

// Every id the server accepts as this person. It builds the same set from
// their verified sign-in: the account id, the email and each sign-in's id. A
// course records its primary instructor under one of them.
export function getOwnUserIds(
  user: Pick<User, 'email' | 'providerData' | 'uid'> | null | undefined,
): string[] {
  if (!user) {
    return [];
  }

  const ids = [
    getKuduUserId(user),
    user.uid,
    user.email,
    ...(user.providerData ?? []).map((provider) => provider.uid),
  ]
    .map((id) => id?.trim())
    .filter((id): id is string => Boolean(id));

  return [...new Set(ids)];
}

export function useCourseStaffInvites(
  courseId: string | undefined,
  // The course's primary instructor, as the course records them.
  instructorId?: string,
): CourseStaffInvitesController {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();
  const userId = getKuduUserId(user);
  const canRemoveStaff = Boolean(
    instructorId && getOwnUserIds(user).includes(instructorId),
  );
  const [invitesRequested, setInvitesRequested] = useState(false);
  // Called each time the Add Instructor dialog opens. Staff and invitations
  // change without this page knowing, when someone accepts an invite in
  // another browser, so both are loaded again instead of shown as they were
  // when the page loaded. Stable, so the dialog can call it from an effect
  // keyed on opening.
  const refresh = useCallback(() => {
    setInvitesRequested(true);

    if (!courseId) {
      return;
    }

    void queryClient.invalidateQueries({ queryKey: staffInvitesQueryKey(courseId) });
    void queryClient.invalidateQueries({ queryKey: courseQueryKey(courseId) });
  }, [courseId, queryClient]);

  const createService = () => {
    if (!user || !userId || !courseId) {
      throw new Error('You must be signed in to manage course staff.');
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;

    return {
      courseId,
      service: createStaffInviteService(
        createApiClient({
          baseUrl: import.meta.env.VITE_API_BASE_URL || '',
          getIdToken,
          getLegacyToken: async () => createLegacyKuduToken(user),
          onAuthError: () =>
            navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
              replace: true,
            }),
        }),
      ),
      userId,
    };
  };

  const listKey = staffInvitesQueryKey(courseId ?? '');

  const invitesQuery = useQuery<StaffInvite[], ApiError>({
    enabled: invitesRequested && Boolean(courseId && userId),
    queryFn: () => {
      const { service, ...ids } = createService();
      return service.listInvites(ids);
    },
    queryKey: listKey,
  });

  const refreshInvites = () => queryClient.invalidateQueries({ queryKey: listKey });

  const createMutation = useMutation<CreatedStaffInvite, ApiError, CreateStaffInviteInput>({
    mutationFn: (input) => {
      const { service, ...ids } = createService();
      return service.createInvite({ ...input, ...ids });
    },
    onSuccess: refreshInvites,
  });

  const renewMutation = useMutation<StaffInvite, ApiError, string>({
    mutationFn: (key) => {
      const { service, ...ids } = createService();
      return service.renewInvite({ ...ids, key });
    },
    onSuccess: refreshInvites,
  });

  const cancelMutation = useMutation<void, ApiError, string>({
    mutationFn: (key) => {
      const { service, ...ids } = createService();
      return service.cancelInvite({ ...ids, key });
    },
    onSuccess: refreshInvites,
  });

  const removeMutation = useMutation<
    void,
    ApiError,
    { role: StaffRole; staffUserId: string }
  >({
    mutationFn: ({ role, staffUserId }) => {
      const { service, ...ids } = createService();
      // The server removes staff only for the course's instructor id, and
      // checks that id against the caller's verified sign-in.
      return service.removeStaffMember({
        ...ids,
        role,
        staffUserId,
        ...(canRemoveStaff && instructorId ? { userId: instructorId } : {}),
      });
    },
    onSuccess: async () => {
      if (courseId) {
        await queryClient.invalidateQueries({ queryKey: courseQueryKey(courseId) });
      }
    },
  });

  return {
    cancelInvite: cancelMutation.mutateAsync,
    canRemoveStaff,
    createInvite: createMutation.mutateAsync,
    currentUserId: userId,
    invites: invitesQuery.data ?? [],
    invitesError: invitesQuery.error ?? null,
    isCreating: createMutation.isPending,
    isLoadingInvites: invitesQuery.isLoading,
    refresh,
    removeStaffMember: (staffUserId, role) =>
      removeMutation.mutateAsync({ role, staffUserId }),
    renewInvite: renewMutation.mutateAsync,
  };
}
